import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IGroupAttendanceSummary, useGroupAttendanceSummary } from 'core/hooks';

import { IOverviewWidgetProps } from './types';
import { OverviewWidgetShell } from './OverviewWidget';
import { Metric, MetricLabel, MetricValue } from './overview.styled';

/**
 * OverviewAttendanceWidget (Requirement 1) — the attendance health card on the
 * reworked Overview tab.
 *
 * Fed by `useGroupAttendanceSummary(groupId)` and reuses the exact KPIs the
 * `AttendanceCard` already computes (Requirement 1.2) — lessons passed/total,
 * average attendance rounded to two decimals, and the below-threshold count —
 * rather than inventing new health logic. Clicking (or pressing Enter/Space)
 * navigates to the Schedule tab where the full attendance breakdown lives
 * (Requirement 2.4).
 */
export const OverviewAttendanceWidget = memo(
  ({ groupId, onNavigate }: IOverviewWidgetProps) => {
    const { t } = useTranslation();
    const { data, loading } = useGroupAttendanceSummary(groupId);

    return (
      <OverviewWidgetShell<IGroupAttendanceSummary>
        title={t('pages.groupDetail.attendance.cardTitle')}
        ariaLabel={t('pages.groupDetail.overview.openAttendance')}
        state={{ data, loading, isEmpty: !data }}
        onActivate={() => onNavigate('schedule')}
      >
        {(summary) => {
          // Mirror AttendanceCard's computations exactly (do not re-derive
          // health): passed/total, average rounded to 2 decimals, below count.
          const totalLessons = summary.totalLessons ?? 0;
          const passedLessons = summary.passedLessons ?? 0;
          const avg =
            Math.round(((summary.averageAttendance ?? 0) as number) * 100) /
            100;
          const below = summary.studentsBelowThresholdCount ?? 0;
          const threshold = summary.threshold ?? 50;

          return (
            <>
              <Metric>
                <MetricLabel>
                  {t('pages.groupDetail.attendance.kpi.lessons')}
                </MetricLabel>
                <MetricValue>{`${passedLessons} / ${totalLessons}`}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>
                  {t('pages.groupDetail.attendance.kpi.average')}
                </MetricLabel>
                <MetricValue tone={avg >= threshold ? 'success' : 'warn'}>
                  {`${avg}%`}
                </MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>
                  {t('pages.groupDetail.attendance.kpi.belowThreshold', {
                    threshold,
                  })}
                </MetricLabel>
                <MetricValue tone={below > 0 ? 'danger' : undefined}>
                  {below}
                </MetricValue>
              </Metric>
            </>
          );
        }}
      </OverviewWidgetShell>
    );
  },
);
